import { FC, useState, useEffect, useMemo } from 'react';
import { Prospect, StatusClientEnum } from 'types';
import CustomDataGrid from 'components/CustomDataGrid';
import { SmartToyOutlined } from '@mui/icons-material/';
import { useNavigate } from 'react-router-dom';
import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { GET_PROMOTERS_BY_STATUS } from '../queries';
import { Button } from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import { StyledChip } from './Chip';
import { actions } from 'redux/ProspectReducer';
import { useDispatch } from 'react-redux';
import {
  FormattedDateEnum,
  formattedDate,
  removeAccents,
  INITIAL_ROWS_PER_PAGE,
  getInitialStateGrid,
  optionsRowsPerPage,
  convertToMexicoCentralTime,
} from 'utils';
import { NetworkError, NoData, RequestError } from 'components/CustomDataGrid/EmptyStates';
import dayjs from 'dayjs';

const NO_PROSPECTS = 0;
const STEP_ID = 1;

type Props = {
  totalItems: number;
  filter: string;
  actualStep: number;
};

const Pending: FC<Props> = ({ totalItems, actualStep, filter }) => {
  const [allProspects, setAllProspects] = useState<Prospect[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE);
  const navigate = useNavigate();
  const [isOnlineState, setIsOnlineState] = useState(navigator.onLine);
  const { setProspectDataReducer } = actions;
  const dispatch = useDispatch();
  const initialStateGrid = getInitialStateGrid({ page, rowsPerPage });

  // Fetch all pending prospects at once
  const { loading, data, error, refetch } = useQuery(GET_PROMOTERS_BY_STATUS, {
    fetchPolicy: 'no-cache',
    variables: {
      page: 0,
      limit: 10000,
      search: '',
      orderBy: 'createdAt',
      statuses: [StatusClientEnum.PENDING],
    },
  });

  useEffect(() => {
    if (!loading && data) {
      setAllProspects(data?.getClientsListByStatus || []);
    }
  }, [data, loading]);

  const filteredProspects = useMemo(() => {
    if (!filter) return allProspects;
    return allProspects.filter((p) => {
      const fullName = `${p.firstName} ${p.firstLastName} ${p.secondLastName}`.toLowerCase();
      const promoter = (p.createdBy || '').toLowerCase();
      const f = filter.toLowerCase();
      return fullName.includes(f) || promoter.includes(f);
    });
  }, [allProspects, filter]);

  const paginatedProspects = useMemo(
    () => filteredProspects.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredProspects, page, rowsPerPage],
  );

  const pendingColumns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Nombre',
      flex: 1.2,
      valueGetter: (params) => {
        const { firstName, firstLastName, secondLastName } = params.row;
        const fullName = removeAccents(`${firstName} ${firstLastName} ${secondLastName}`);
        return `${fullName}`;
      },
      renderCell: ({ row }: GridRenderCellParams) => {
        const { firstName, firstLastName, secondLastName } = row;
        return `${firstName} ${firstLastName} ${secondLastName}`;
      },
    },
    {
      field: 'createdBy',
      headerName: 'Promotora',
      flex: 1.2,
      valueGetter: ({ row }: GridRenderCellParams) => {
        const { createdBy } = row;
        return createdBy;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de solicitud',
      flex: 0.7,
      valueFormatter: ({ value }: GridValueFormatterParams) => {
        if (!value) return '';
        return dayjs.utc(value).tz('America/Mexico_City').format('DD/MM/YYYY HH:mm');
      },
    },
    {
      field: 'status',
      headerName: 'Estatus Verificación Automatizada',
      flex: 0.7,
      renderCell: ({ row }: GridRenderCellParams) => {
        let statusMessage = '';
        let status = StatusClientEnum.CREATED;
        if (row.status === StatusClientEnum.ACCEPTED) {
          statusMessage = 'Aceptado';
          status = StatusClientEnum.ACCEPTED;
        } else if (row.status === StatusClientEnum.REJECTED) {
          statusMessage = 'Rechazado';
          status = StatusClientEnum.REJECTED;
        } else {
          statusMessage = 'Pendiente';
        }
        return <StyledChip icon={<SmartToyOutlined />} label={statusMessage} state={status} />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      flex: 0.5,
      sortable: false,
      disableColumnMenu: true,
      filterable: false,
      align: 'right',
      getActions: ({ row }: GridRowParams) => [
        <Button
          key={row.id}
          variant={'outlined'}
          color={'primary'}
          onClick={() => {
            navigate(`/validate/${row.id}`);
            dispatch(setProspectDataReducer(row));
          }}
        >
          Verificar
        </Button>,
      ],
    },
  ];

  const handleChangePage = ({ page, pageSize }: GridPaginationModel) => {
    setPage(page);
    setRowsPerPage(pageSize);
  };

  useEffect(() => {
    if (actualStep === STEP_ID) {
      setPage(0);
    }
  }, [actualStep]);

  if (!isOnlineState) return <NetworkError handleRetry={refetch} />;
  if (error) return <RequestError handleRetry={refetch} />;
  if (allProspects.length === 0 && loading === false)
    return <NoData message="No hay ningún prospecto pendiente para verificar" />;

  return (
    <CustomDataGrid
      loading={loading}
      data={paginatedProspects}
      dataColumns={pendingColumns}
      nameFilter={filter}
      title="Listado de prospectos pendientes de verificar"
      totalRows={filteredProspects.length}
      handleChangePage={handleChangePage}
      initialState={initialStateGrid}
      page={page}
      pageSize={rowsPerPage}
      pageSizeOptions={optionsRowsPerPage}
    />
  );
};

export default Pending;
